// combatpro/encounter/encounterGenEngine.js
//
// Combat PRO - Random & Wave Encounter GENERATION engine (pure).
//
// The single pure, framework-independent core of the Random + Wave encounter
// systems (spec: COMBAT_PRO_RANDOM_WAVE_ENCOUNTER_SYSTEM_2026-09-04.md sections
// 8-19, 29). Data in, data out: it filters the existing 300+ monster catalogue
// (window.VI_MONSTERS_DB shape { name, cr, type, ... }), applies preset difficulty
// / count / creature-type / composition rules against a standard 5e CR->XP budget,
// and returns a chosen combination of EXISTING catalogue objects (never mutated,
// never duplicated as new canonical records). It owns NO React, NO DOM, NO context,
// NO state, NO storage, NO instance construction (buildSelectedSlot owns that),
// NO trigger/group records (the commit layer + existing dmOps/encounterBrief engines
// own those). It only decides WHICH catalogue monsters, in WHAT counts, in WHICH
// waves. No optional chaining. No nullish coalescing. No em dash.
//
// Determinism: every generator accepts opts.rng (a 0..1 function). Tests pass a
// seeded rng (makeRng) so family/slot mapping, filter correctness, the 16 cap,
// wave partitioning, style behaviour and no-mutation are all provable.

// Shared Combat PRO threat authority (spec correction #4): the generator REUSES
// Live Threat's CR primitive and difficulty computation rather than defining its
// own. crToNumber + computeThreat are the single source; the CR->XP budget below
// is only a generator-specific SELECTION helper built on that shared primitive.
import { crToNumber, computeThreat } from '../runtime/dmOpsEngine';
export { crToNumber };

// ---- Preset vocabularies (spec 8, 13) --------------------------------------

export var MONSTER_CAP = 16; // existing Combat PRO monster-instance cap (spec 8.3, 13.3).

export var DIFFICULTIES = ['easy', 'medium', 'hard', 'deadly', 'random'];
export var DIFFICULTY_LABELS = { easy: 'Easy', medium: 'Medium', hard: 'Hard', deadly: 'Deadly', random: 'Random' };

export var CREATURE_TYPES = ['any', 'mixed', 'aberration', 'beast', 'celestial', 'construct', 'dragon', 'elemental', 'fey', 'fiend', 'giant', 'humanoid', 'monstrosity', 'ooze', 'plant', 'undead'];
export var CREATURE_TYPE_LABELS = {
  any: 'Any', mixed: 'Mixed', aberration: 'Aberration', beast: 'Beast', celestial: 'Celestial',
  construct: 'Construct', dragon: 'Dragon', elemental: 'Elemental', fey: 'Fey', fiend: 'Fiend',
  giant: 'Giant', humanoid: 'Humanoid', monstrosity: 'Monstrosity', ooze: 'Ooze', plant: 'Plant', undead: 'Undead'
};

// Environment metadata is NOT present in the existing catalogue (spec 8.5 / 20.4:
// do not invent a parallel taxonomy). ANY is therefore the only data-backed choice.
export var ENVIRONMENTS = ['any'];
export var ENVIRONMENT_LABELS = { any: 'Any' };

// Source choices backed by real data: ALL / OFFICIAL / MY MONSTERS (custom pool).
// FAVOURITES is exposed only when favourite data exists (resolved by the hook).
export var SOURCES = ['all', 'official', 'mine', 'favourites'];
export var SOURCE_LABELS = { all: 'All', official: 'Official', mine: 'My Monsters', favourites: 'Favourites' };

export var COMPOSITIONS = ['random', 'pack', 'mixed', 'elite', 'boss-minions', 'ambush'];
export var COMPOSITION_LABELS = {
  random: 'Random', pack: 'Pack', mixed: 'Mixed', elite: 'Elite', 'boss-minions': 'Boss + Minions', ambush: 'Ambush'
};

export var WAVE_COUNTS = ['2', '3', '4', 'random'];
export var WAVE_STYLES = ['standard', 'escalating', 'swarm', 'boss-finale', 'elite-finale', 'ambush', 'attrition', 'reinforcements', 'random'];
export var WAVE_STYLE_LABELS = {
  standard: 'Standard', escalating: 'Escalating', swarm: 'Swarm', 'boss-finale': 'Boss Finale',
  'elite-finale': 'Elite Finale', ambush: 'Ambush', attrition: 'Attrition', reinforcements: 'Reinforcements', random: 'Random'
};

// Quick presets (spec 19): each just populates the normal controls.
export var RANDOM_QUICK_PRESETS = [
  { id: 'quick-easy', label: 'Quick Easy', values: { difficulty: 'easy', count: 3, creatureType: 'any', composition: 'random' } },
  { id: 'standard-fight', label: 'Standard Fight', values: { difficulty: 'medium', count: 4, creatureType: 'any', composition: 'mixed' } },
  { id: 'hard-surprise', label: 'Hard Surprise', values: { difficulty: 'hard', count: 5, creatureType: 'any', composition: 'ambush' } },
  { id: 'deadly-surprise', label: 'Deadly Surprise', values: { difficulty: 'deadly', count: 4, creatureType: 'any', composition: 'boss-minions' } },
  { id: 'random-everything', label: 'Random Everything', values: { difficulty: 'random', count: 0, creatureType: 'any', composition: 'random' } }
];

export var WAVE_QUICK_PRESETS = [
  { id: 'standard-2', label: 'Standard 2-Wave', values: { waves: '2', style: 'standard', difficulty: 'medium', totalMonsters: 6 } },
  { id: 'escalating-3', label: 'Escalating 3-Wave', values: { waves: '3', style: 'escalating', difficulty: 'hard', totalMonsters: 9 } },
  { id: 'boss-finale', label: 'Boss Finale', values: { waves: '3', style: 'boss-finale', difficulty: 'hard', totalMonsters: 10 } },
  { id: 'reinforcements', label: 'Reinforcements', values: { waves: '3', style: 'reinforcements', difficulty: 'medium', totalMonsters: 9 } },
  { id: 'random-waves', label: 'Random Waves', values: { waves: 'random', style: 'random', difficulty: 'random', totalMonsters: 0 } }
];

// ---- 5e CR / XP math (spec 8.1, 9: reuse existing CR data; the generation-time
//      budget is the generator's own math, not a second live-threat engine) ------

var CR_XP = {
  '0': 10, '1/8': 25, '1/4': 50, '1/2': 100, '1': 200, '2': 450, '3': 700, '4': 1100,
  '5': 1800, '6': 2300, '7': 2900, '8': 3900, '9': 5000, '10': 5900, '11': 7200, '12': 8400,
  '13': 10000, '14': 11500, '15': 13000, '16': 15000, '17': 18000, '18': 20000, '19': 22000,
  '20': 25000, '21': 33000, '22': 41000, '23': 50000, '24': 62000, '25': 75000, '26': 90000,
  '27': 105000, '28': 120000, '29': 135000, '30': 155000
};

// XP thresholds per single character level: [easy, medium, hard, deadly].
var XP_THRESHOLDS = {
  1: [25, 50, 75, 100], 2: [50, 100, 150, 200], 3: [75, 150, 225, 400], 4: [125, 250, 375, 500],
  5: [250, 500, 750, 1100], 6: [300, 600, 900, 1400], 7: [350, 750, 1100, 1700], 8: [450, 900, 1400, 2100],
  9: [550, 1100, 1600, 2400], 10: [600, 1200, 1900, 2900], 11: [800, 1600, 2400, 3600], 12: [1000, 2000, 3000, 4500],
  13: [1100, 2200, 3400, 5100], 14: [1250, 2500, 3800, 5700], 15: [1400, 2800, 4300, 6400], 16: [1600, 3200, 4800, 7200],
  17: [2000, 3900, 5900, 8800], 18: [2100, 4200, 6300, 9500], 19: [2400, 4900, 7300, 10900], 20: [2800, 5700, 8500, 12700]
};

var DIFF_INDEX = { easy: 0, medium: 1, hard: 2, deadly: 3 };

// crToNumber is imported from dmOpsEngine (shared threat primitive) - see top.

// XP value for a monster CR. Exact table hit first; else nearest table CR by number.
export function crToXp(cr) {
  var key = String(cr == null ? '' : cr).trim();
  if (CR_XP.hasOwnProperty(key)) { return CR_XP[key]; }
  var n = crToNumber(cr);
  var best = 10; var bestDist = Infinity;
  for (var k in CR_XP) {
    if (!CR_XP.hasOwnProperty(k)) { continue; }
    var d = Math.abs(crToNumber(k) - n);
    if (d < bestDist) { bestDist = d; best = CR_XP[k]; }
  }
  return best;
}

export function clampLevel(level) {
  var n = parseInt(level, 10);
  if (isNaN(n)) { return 5; }
  if (n < 1) { return 1; }
  if (n > 20) { return 20; }
  return n;
}

// Single-character threshold for a level+difficulty.
export function xpThreshold(level, difficulty) {
  var lv = clampLevel(level);
  var row = XP_THRESHOLDS[lv] || XP_THRESHOLDS[5];
  var di = DIFF_INDEX.hasOwnProperty(difficulty) ? DIFF_INDEX[difficulty] : 1;
  return row[di];
}

// 5e encounter multiplier by number of monsters.
export function encounterMultiplier(count) {
  var c = parseInt(count, 10);
  if (isNaN(c) || c <= 1) { return 1; }
  if (c === 2) { return 1.5; }
  if (c <= 6) { return 2; }
  if (c <= 10) { return 2.5; }
  if (c <= 14) { return 3; }
  return 4;
}

// Total party ADJUSTED-XP budget for a difficulty (threshold * party size).
export function partyBudget(level, difficulty, partySize) {
  var ps = parseInt(partySize, 10);
  if (isNaN(ps) || ps < 1) { ps = 4; }
  return xpThreshold(level, difficulty) * ps;
}

// Difficulty band via the SHARED Live-Threat authority (computeThreat), so a
// generated encounter's reported difficulty matches Combat PRO Live Threat for the
// same party/monster state (spec correction #4). No separate difficulty authority.
export function threatBand(picks, partySize) {
  var ps = parseInt(partySize, 10);
  if (isNaN(ps) || ps < 1) { ps = 4; }
  var players = [];
  for (var i = 0; i < ps; i++) { players.push({}); }
  return computeThreat({ players: players, monsters: Array.isArray(picks) ? picks : [] }).band;
}

// ---- Seeded RNG (mulberry32) so generation is deterministic under test ------

export function makeRng(seed) {
  var a = (typeof seed === 'number') ? (seed >>> 0) : 0x9e3779b9;
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    var t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function rngOf(opts) {
  return (opts && typeof opts.rng === 'function') ? opts.rng : Math.random;
}

// ---- Catalogue filtering (spec 8.4/8.5/8.6, 9) ------------------------------

// Eligible catalogue by creature type. 'any'/'mixed' allow every type. A specific
// type is a HARD filter: an entry with no/other type is excluded (spec 9: do not
// silently violate the selected creature type). Source/environment are resolved
// upstream by the hook (source needs the official-vs-custom split; no environment
// data exists), so this engine takes an already source-scoped pool.
// Canonical creature type for a monster: the explicit `type` field when present,
// else derived from the canonical `race` field ("Fiend (Demon)" -> "fiend",
// "Humanoid (Any)" -> "humanoid"). This reads EXISTING catalogue fields only - it
// does not invent metadata or maintain a parallel catalogue (spec correction #2).
export function monsterType(m) {
  if (!m) { return ''; }
  if (m.type && String(m.type).trim()) { return String(m.type).trim().toLowerCase(); }
  var race = m.race ? String(m.race).trim() : '';
  if (!race) { return ''; }
  return race.split('(')[0].trim().toLowerCase();
}
export function eligibleByType(pool, creatureType) {
  var arr = Array.isArray(pool) ? pool : [];
  var t = String(creatureType == null ? 'any' : creatureType).toLowerCase();
  if (t === 'any' || t === 'mixed') {
    return arr.filter(function (m) { return m && m.name; });
  }
  return arr.filter(function (m) { return m && m.name && monsterType(m) === t; });
}

// ---- Picking primitives -----------------------------------------------------

function xpOf(m) { return crToXp(m ? m.cr : 0); }

// Nearest-K weighted pick toward a target XP. Deterministic under a seeded rng.
// avoidNames (optional) is a set of names to deprioritise (reroll variety).
function pickNear(pool, targetXp, rng, avoidNames) {
  if (!pool.length) { return null; }
  var scored = pool.map(function (m) { return { m: m, d: Math.abs(xpOf(m) - targetXp) }; });
  scored.sort(function (a, b) { return a.d - b.d; });
  var k = Math.min(6, scored.length);
  var band = scored.slice(0, k);
  // Prefer entries not in avoidNames when alternatives exist.
  if (avoidNames && band.length > 1) {
    var filtered = band.filter(function (x) { return avoidNames.indexOf(x.m.name) === -1; });
    if (filtered.length) { band = filtered; }
  }
  // Weight inversely by distance rank (closest heaviest) with a floor for variety.
  var weights = band.map(function (x, i) { return (band.length - i) + 1; });
  var total = 0; for (var i = 0; i < weights.length; i++) { total += weights[i]; }
  var r = rng() * total;
  for (var j = 0; j < band.length; j++) {
    r -= weights[j];
    if (r <= 0) { return band[j].m; }
  }
  return band[band.length - 1].m;
}

// Pick preferring a type not already used (for MIXED / distinct spread).
function pickDistinct(pool, targetXp, rng, usedTypes) {
  var fresh = pool.filter(function (m) { return usedTypes.indexOf(monsterType(m)) === -1; });
  var from = fresh.length ? fresh : pool;
  return pickNear(from, targetXp, rng, null);
}

// Summarise a flat monster list as "Goblin x4, Ogre x1" (stable order of first sight).
export function summarizePicks(monsters) {
  var order = [];
  var counts = {};
  for (var i = 0; i < monsters.length; i++) {
    var nm = monsters[i] && monsters[i].name ? monsters[i].name : 'Monster';
    if (!counts[nm]) { counts[nm] = 0; order.push(nm); }
    counts[nm]++;
  }
  return order.map(function (nm) { return nm + ' \u00d7' + counts[nm]; }).join(', ');
}

// Signature of a pick set (for avoid-immediate-repeat, spec 20.2).
export function pickSignature(monsters) {
  var m = {};
  for (var i = 0; i < monsters.length; i++) {
    var nm = monsters[i] && monsters[i].name ? monsters[i].name : '?';
    m[nm] = (m[nm] || 0) + 1;
  }
  var keys = Object.keys(m).sort();
  return keys.map(function (k) { return k + ':' + m[k]; }).join('|');
}

// ---- Random encounter generation (spec 8-10) --------------------------------

// generateRandom(opts):
//   pool         already source-scoped catalogue objects (hook provides).
//   level        1-20 (or resolved from party by the hook).
//   partySize    number of players (budget scaling).
//   difficulty   easy|medium|hard|deadly|random.
//   count        1-16 (0 => auto: derive a sensible count from budget).
//   creatureType any|mixed|<type>.
//   composition  random|pack|mixed|elite|boss-minions|ambush.
//   rng, avoidSignature (optional).
// Returns { ok, monsters:[catalogueObj...], count, summary, adjustedXp, budget,
//           band, composition, difficulty } or { ok:false, error, reason }.
export function generateRandom(opts) {
  var o = opts || {};
  var rng = rngOf(o);
  var difficulty = o.difficulty === 'random' ? DIFFICULTIES[Math.floor(rng() * 4)] : (DIFF_INDEX.hasOwnProperty(o.difficulty) ? o.difficulty : 'medium');
  var level = clampLevel(o.level);
  var partySize = (parseInt(o.partySize, 10) > 0) ? parseInt(o.partySize, 10) : 4;
  var eligible = eligibleByType(o.pool, o.creatureType);
  if (!eligible.length) {
    return { ok: false, error: 'NO_VALID_MONSTERS', reason: 'creature type / source', difficulty: difficulty };
  }
  var budget = (typeof o.budget === 'number' && o.budget > 0) ? o.budget : partyBudget(level, difficulty, partySize);

  var count = parseInt(o.count, 10);
  if (isNaN(count) || count < 1) {
    // Auto count: aim for a small group sized to the budget (2-5), then refine.
    count = 4;
  }
  if (count > MONSTER_CAP) { count = MONSTER_CAP; }

  var composition = (COMPOSITIONS.indexOf(o.composition) !== -1) ? o.composition : 'random';
  if (composition === 'boss-minions' && count < 2) { count = 2; }

  var mult = encounterMultiplier(count);
  var rawTarget = budget / mult;          // sum of raw monster XP to aim for
  var perTarget = rawTarget / count;      // per-monster raw XP target
  var avoid = (o.avoidSignature ? String(o.avoidSignature).split('|').map(function (p) { return p.split(':')[0]; }) : null);

  var picks = [];
  var usedTypes = [];
  var i;
  if (composition === 'pack') {
    var base = pickNear(eligible, perTarget, rng, avoid);
    for (i = 0; i < count; i++) { picks.push(base); }
  } else if (composition === 'boss-minions') {
    var boss = pickNear(eligible, rawTarget * 0.55, rng, avoid);
    picks.push(boss);
    var minionTarget = Math.max(10, (rawTarget - xpOf(boss)) / Math.max(1, count - 1));
    for (i = 0; i < count - 1; i++) { picks.push(pickNear(eligible, minionTarget, rng, null)); }
  } else if (composition === 'elite') {
    for (i = 0; i < count; i++) { picks.push(pickNear(eligible, perTarget * 1.35, rng, avoid)); }
  } else if (composition === 'mixed' || composition === 'ambush') {
    for (i = 0; i < count; i++) {
      var pm = pickDistinct(eligible, perTarget, rng, usedTypes);
      if (pm) { usedTypes.push(monsterType(pm)); }
      picks.push(pm);
    }
  } else {
    for (i = 0; i < count; i++) { picks.push(pickNear(eligible, perTarget, rng, avoid)); }
  }

  picks = picks.filter(Boolean);
  if (!picks.length) {
    return { ok: false, error: 'NO_VALID_ENCOUNTER', reason: 'no viable combination', difficulty: difficulty };
  }

  var adjustedXp = 0;
  for (i = 0; i < picks.length; i++) { adjustedXp += xpOf(picks[i]); }
  adjustedXp = Math.round(adjustedXp * encounterMultiplier(picks.length));
  var band = threatBand(picks, partySize);

  return {
    ok: true, monsters: picks, count: picks.length, summary: summarizePicks(picks),
    adjustedXp: adjustedXp, budget: Math.round(budget), band: band,
    composition: composition, difficulty: difficulty, level: level
  };
}

// ---- Wave generation (spec 12-18) -------------------------------------------

export function resolveWaveCount(waves, rng) {
  if (waves === 'random' || waves == null || waves === '') {
    return 2 + Math.floor(rng() * 3); // 2..4
  }
  var n = parseInt(waves, 10);
  if (isNaN(n) || n < 2) { return 2; }
  if (n > 4) { return 4; }
  return n;
}

export function resolveWaveStyle(style, rng) {
  if (style === 'random' || WAVE_STYLES.indexOf(style) === -1) {
    var pickable = WAVE_STYLES.filter(function (s) { return s !== 'random'; });
    return pickable[Math.floor(rng() * pickable.length)];
  }
  return style;
}

// Body-count partition across waves (each >= 1, sum = total), shaped by style.
export function partitionCounts(total, waveCount, style) {
  var t = Math.max(1, Math.min(MONSTER_CAP, parseInt(total, 10) || 1));
  var w = Math.max(1, Math.min(t, parseInt(waveCount, 10) || 1));
  var counts = [];
  var i;
  for (i = 0; i < w; i++) { counts.push(1); }
  var remaining = t - w;
  // Weighting of the LEFTOVER bodies by style.
  var weights = [];
  for (i = 0; i < w; i++) {
    if (style === 'swarm' || style === 'reinforcements') { weights.push(w - i); }          // front-loaded bodies
    else if (style === 'escalating' || style === 'boss-finale' || style === 'elite-finale') { weights.push(i + 1); } // back-loaded bodies
    else if (style === 'ambush') { weights.push(i === 0 ? 1 : 2); }                          // small opener, bigger follow-up
    else { weights.push(1); }                                                                // standard / attrition: even
  }
  var wsum = 0; for (i = 0; i < w; i++) { wsum += weights[i]; }
  for (i = 0; i < w && remaining > 0; i++) {
    var add = Math.round(remaining * (weights[i] / wsum));
    counts[i] += add;
  }
  // Fix rounding drift so the sum is exactly total.
  var have = 0; for (i = 0; i < w; i++) { have += counts[i]; }
  var drift = t - have;
  var idx = 0;
  while (drift !== 0) {
    if (drift > 0) { counts[idx % w]++; drift--; }
    else if (counts[idx % w] > 1) { counts[idx % w]--; drift++; }
    idx++;
    if (idx > 1000) { break; }
  }
  return counts;
}

// Per-wave THREAT weights (sum ~1) shaping how much budget each wave carries.
export function waveWeights(waveCount, style) {
  var w = Math.max(1, parseInt(waveCount, 10) || 1);
  var raw = [];
  var i;
  for (i = 0; i < w; i++) {
    if (style === 'escalating') { raw.push(i + 1); }
    else if (style === 'boss-finale' || style === 'elite-finale') { raw.push(i === w - 1 ? (w + 1) : 1); }
    else if (style === 'reinforcements') { raw.push(w - i); }
    else if (style === 'ambush') { raw.push(i === 0 ? 1 : 2); }
    else { raw.push(1); } // standard / swarm / attrition
  }
  var sum = 0; for (i = 0; i < w; i++) { sum += raw[i]; }
  return raw.map(function (x) { return x / sum; });
}

function styleComposition(style, waveIndex, waveCount) {
  var last = (waveIndex === waveCount - 1);
  if (style === 'boss-finale') { return last ? 'boss-minions' : 'pack'; }
  if (style === 'elite-finale') { return last ? 'elite' : 'random'; }
  if (style === 'swarm') { return 'pack'; }
  if (style === 'escalating') { return last ? 'elite' : 'random'; }
  return 'random';
}

// Trigger spec for wave w>0 (spec 14, 15). Uses ONLY existing event primitives.
export function waveEventSpec(style, waveIndex, waveCount) {
  var human = 'Wave ' + (waveIndex + 1);
  if (style === 'ambush') {
    return { kind: 'reinforcement', triggerType: 'round', round: 2 + waveIndex, note: human + ' ambush follow-up', waveIndex: waveIndex };
  }
  if (style === 'reinforcements') {
    return { kind: 'reinforcement', triggerType: 'round', round: 2 * waveIndex + 2, note: human + ' reinforcements arrive', waveIndex: waveIndex };
  }
  // Default sequential model (spec 15 + correction #3): AUTOMATIC - enters when the
  // preceding Wave Combat Group is resolved by outcome state. Uses the existing
  // 'group-defeated' Encounter Event trigger type; the actual entry is driven by
  // applyWaveProgression (dmOpsEngine). Manual activation remains an override.
  return { kind: 'wave', triggerType: 'group-defeated', round: null, note: 'Automatic: enters when Wave ' + waveIndex + ' is resolved', waveIndex: waveIndex, precedingWave: waveIndex };
}

// generateWave(opts): high-level presets -> a complete staged battle definition.
// Returns { ok, style, waveCount, waves:[{label, groupName, monsters, summary}],
//           events:[waveEventSpec...], summary, waveSummary:[...], totalMonsters,
//           difficulty, level } or { ok:false, error, reason }.
export function generateWave(opts) {
  var o = opts || {};
  var rng = rngOf(o);
  var difficulty = o.difficulty === 'random' ? DIFFICULTIES[Math.floor(rng() * 4)] : (DIFF_INDEX.hasOwnProperty(o.difficulty) ? o.difficulty : 'medium');
  var level = clampLevel(o.level);
  var partySize = (parseInt(o.partySize, 10) > 0) ? parseInt(o.partySize, 10) : 4;
  var eligible = eligibleByType(o.pool, o.creatureType);
  if (!eligible.length) {
    return { ok: false, error: 'NO_VALID_MONSTERS', reason: 'creature type / source', difficulty: difficulty };
  }

  var total = parseInt(o.totalMonsters, 10);
  if (isNaN(total) || total < 1) { total = 8; }
  if (total > MONSTER_CAP) { total = MONSTER_CAP; }

  var style = resolveWaveStyle(o.style, rng);
  var waveCount = resolveWaveCount(o.waves, rng);
  if (waveCount > total) { waveCount = total; } // each wave needs >= 1 body

  var counts = partitionCounts(total, waveCount, style);
  var weights = waveWeights(waveCount, style);
  var budget = partyBudget(level, difficulty, partySize);

  var waves = [];
  var events = [];
  var waveSummary = [];
  for (var w = 0; w < waveCount; w++) {
    var comp = styleComposition(style, w, waveCount);
    var sub = generateRandom({
      pool: eligible, level: level, partySize: partySize, difficulty: difficulty,
      count: counts[w], composition: comp, creatureType: 'any',
      budget: Math.max(10, budget * weights[w]), rng: rng
    });
    var mons = (sub.ok && sub.monsters) ? sub.monsters : [];
    var label = 'Wave ' + (w + 1);
    var sum = summarizePicks(mons);
    waves.push({ label: label, groupName: label, monsters: mons, summary: sum });
    waveSummary.push(label + ' \u2014 ' + (sum || 'none'));
    if (w > 0) { events.push(waveEventSpec(style, w, waveCount)); }
  }

  var totalMons = 0;
  for (var i = 0; i < waves.length; i++) { totalMons += waves[i].monsters.length; }

  return {
    ok: true, style: style, waveCount: waveCount, waves: waves, events: events,
    summary: summarizeWave(difficulty, level, waveCount, totalMons, style),
    waveSummary: waveSummary, totalMonsters: totalMons, difficulty: difficulty, level: level
  };
}

// One-line wave headline (spec 18): "HARD - LEVEL 7 - 3 WAVES - 11 MONSTERS - BOSS FINALE".
export function summarizeWave(difficulty, level, waveCount, totalMonsters, style) {
  return [
    (DIFFICULTY_LABELS[difficulty] || 'Medium').toUpperCase(),
    'LEVEL ' + clampLevel(level),
    waveCount + ' WAVES',
    totalMonsters + ' MONSTERS',
    (WAVE_STYLE_LABELS[style] || 'Standard').toUpperCase()
  ].join(' \u2022 ');
}

// One-line random headline: "MEDIUM - LEVEL 5 - 4 MONSTERS - MIXED".
export function summarizeRandom(difficulty, level, count, composition) {
  return [
    (DIFFICULTY_LABELS[difficulty] || 'Medium').toUpperCase(),
    'LEVEL ' + clampLevel(level),
    count + ' MONSTERS',
    (COMPOSITION_LABELS[composition] || 'Random').toUpperCase()
  ].join(' \u2022 ');
}
